import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

import { KickstartService } from '../../../../../core/kickstart/kickstart.service';
import { UPLOAD_ACCEPT } from '../../../shared/doc-drop/doc-drop';
import { BusinessAiConsentService } from '../../../../../core/attestations/business-ai-consent.service';
import {
  KsNamedAccount,
  KsOutstandingItem,
  KsTaxPin,
} from '../../../../../core/kickstart/kickstart.types';
import { GENERIC_ERROR } from '../../../../../core/http/read-error';

// Pure, so the agent's application page can read the same line (FLOW-344).
export { progressLine } from './progress-line';

/**
 * DOC-73 UI-DA-73-02 — the two panels under the status, each shown only when
 * it has items: what the business owes, with the act to give it on this page,
 * and what CapX is working on (no date, no countdown, no red). Every item, its
 * owner and its words come from the back end; this component decides none.
 */
@Component({
  selector: 'app-ks-outstanding',
  imports: [
    DatePipe,
    NgTemplateOutlet,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
  ],
  templateUrl: './ks-outstanding.html',
  styleUrl: './ks-outstanding.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KsOutstandingComponent {
  private readonly kickstart = inject(KickstartService);
  private readonly consent = inject(BusinessAiConsentService);
  private readonly destroyRef = inject(DestroyRef);
  /** The picker offers only what the server takes (`files/services.py`). */
  protected readonly accept = UPLOAD_ACCEPT;

  readonly shortId = input.required<string>();
  readonly entityShortId = input.required<string>();
  readonly items = input<readonly KsOutstandingItem[]>([]);
  /** FLOW-352 (DOC-83) — every tax compliance PIN sent, newest first. */
  readonly taxPins = input<readonly KsTaxPin[]>([]);
  /** A PIN may be sent now, a later one too — the back end's call. */
  readonly taxPinOpen = input(false);
  /** An act landed — the parent re-reads the application, and with it the list. */
  readonly changed = output<void>();

  protected readonly business = computed(() => this.items().filter((i) => i.owner === 'BUSINESS'));
  protected readonly capx = computed(() => this.items().filter((i) => i.owner === 'CAPX'));

  protected readonly busyKey = signal<string | null>(null);
  protected readonly error = signal<string | null>(null);

  // The account confirmation's "No" rows (DOC-72 UI-DA-72-04's shape).
  protected readonly namingAccounts = signal(false);
  protected readonly accountRows = signal<KsNamedAccount[]>([
    { bank_name: '', last_four: '', reason: '' },
  ]);
  protected readonly pin = signal('');
  /** *Send a new PIN* opened the box beneath the PINs already sent. */
  protected readonly newPin = signal(false);
  protected readonly pinOwed = computed(() => this.items().some((i) => i.act === 'TAX_PIN'));

  protected upload(item: KsOutstandingItem, event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const files = Array.from(inputEl.files ?? []);
    inputEl.value = '';
    if (!files.length) return;
    // A named account's statements — the one upload an item offers (FLOW-381).
    // One file at a time keeps each upload its own recorded entry.
    files.forEach((file) =>
      this.run(item.key, this.kickstart.uploadDocument(this.shortId(), file, 'BANK_STATEMENT')),
    );
  }

  protected confirmAll(item: KsOutstandingItem): void {
    this.run(item.key, this.kickstart.confirmAccounts(this.shortId(), 'ALL_UPLOADED'));
  }

  protected updateRow(index: number, field: keyof KsNamedAccount, value: string): void {
    this.accountRows.update((rows) =>
      rows.map((r, i) => (i === index ? { ...r, [field]: value } : r)),
    );
  }

  protected addRow(): void {
    this.accountRows.update((rows) => [...rows, { bank_name: '', last_four: '', reason: '' }]);
  }

  protected readonly rowsReady = computed(() =>
    this.accountRows().every((r) => r.bank_name.trim() && r.reason.trim()),
  );

  protected nameAccounts(item: KsOutstandingItem): void {
    if (!this.rowsReady()) return;
    this.run(
      item.key,
      this.kickstart.confirmAccounts(this.shortId(), 'NOT_ALL', this.accountRows()),
    );
  }

  protected giveConsent(item: KsOutstandingItem): void {
    this.run(item.key, this.consent.give(this.entityShortId()));
  }

  /** A PIN is a new entry every time — an earlier one is never replaced. */
  protected sendPin(): void {
    const pin = this.pin().trim();
    if (!pin) return;
    this.run('tax_pin', this.kickstart.sendTaxPin(this.shortId(), pin), () => {
      this.pin.set('');
      this.newPin.set(false);
    });
  }

  private run(key: string, call$: Observable<unknown>, after?: () => void): void {
    this.busyKey.set(key);
    this.error.set(null);
    call$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.busyKey.set(null);
        this.namingAccounts.set(false);
        after?.();
        this.changed.emit();
      },
      error: (err: unknown) => {
        this.busyKey.set(null);
        const detail = (err as { error?: { detail?: unknown } })?.error?.detail;
        this.error.set(typeof detail === 'string' ? detail : GENERIC_ERROR);
      },
    });
  }
}
